import { ReactNode } from 'react'

import { Box, Spacer } from '@hivewatch/dark'

import { IncidentActionType, IncidentStatus } from '@/graphql/generated/schemas'
import { creatorRoleName } from '@/utils/formats'

import {
  CannedIncidentActionType,
  ExtendedIncidentActionType,
  NonActionItem,
  PriorityChangeHistory,
} from '../../../shared/types/feedTypes'
import { formatEmailRecipientCount } from '../../../shared/utils/feedUtils'
import { IncidentStatusTag } from './IncidentStatus'

interface ActionHeaderIProps {
  actionType:
    | IncidentActionType
    | NonActionItem
    | CannedIncidentActionType
    | ExtendedIncidentActionType
  username?: string
  roleName?: string
  systemMessage?: string
  technicianName?: string
  history?: PriorityChangeHistory
  isRuleAction?: boolean
  emailRecipientCount?: number
  onRuleClick?: () => void
  /** When false, omits the inline IncidentStatusTag from status-changing actions */
  shouldShowStatusTag?: boolean
}

const formatPriorityValue = (value: string | null | undefined): string => {
  if (!value) return 'No Priority'
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

export const ActionHeader = ({
  actionType,
  username,
  roleName,
  systemMessage,
  technicianName,
  history,
  isRuleAction,
  emailRecipientCount,
  onRuleClick,
  shouldShowStatusTag = true,
}: ActionHeaderIProps) => {
  switch (actionType) {
    case IncidentActionType.AcknowledgementByGuard:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Acknowledged{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.TakeoverByGuard:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Took Over{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.AcknowledgementByOperator:
      return (
        <Box direction='row' display='flex'>
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#2E6CDB' fontWeight='bold'>
              Acknowledged{' '}
            </Box>
            the Incident.
          </Box>
          {shouldShowStatusTag && (
            <>
              <Spacer />
              <Box ml='5px'>
                <IncidentStatusTag status={IncidentStatus.InProgress} />
              </Box>
            </>
          )}
        </Box>
      )
    case IncidentActionType.OnScene:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          is{' '}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            On Scene.
          </Box>
        </Box>
      )
    case IncidentActionType.ReleaseByGuard:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Released{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.ReleaseByOperator:
      return (
        <Box direction='row' display='flex'>
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#2E6CDB' fontWeight='bold'>
              Released{' '}
            </Box>
            the Incident.
          </Box>
          {shouldShowStatusTag && (
            <>
              <Spacer />
              <Box ml='5px'>
                <IncidentStatusTag status={IncidentStatus.Active} />
              </Box>
            </>
          )}
        </Box>
      )
    case IncidentActionType.Cleared:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#13B0B0' fontWeight='bold'>
            Cleared{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.Resolved:
      return (
        <Box direction='row' display='flex'>
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#12B779' fontWeight='bold'>
              Resolved{' '}
            </Box>
            the Incident.
          </Box>
          {shouldShowStatusTag && (
            <>
              <Spacer />
              <Box ml='5px'>
                <IncidentStatusTag status={IncidentStatus.Resolved} />
              </Box>
            </>
          )}
        </Box>
      )
    case IncidentActionType.OrderDeviceRepair:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#13B0B0' fontWeight='bold'>
            contacted Technician{' '}
          </Box>
          {technicianName}.
        </Box>
      )
    case IncidentActionType.SetRealToFalse:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          has marked the Incident as{' '}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            False.
          </Box>
        </Box>
      )
    case IncidentActionType.SetRealToTrue:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          has marked the Incident as{' '}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Real.
          </Box>
        </Box>
      )
    case IncidentActionType.StartEmergencyCall:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          called Emergency Number
        </Box>
      )
    case IncidentActionType.EndEmergencyCall:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username}`}
          </Box>
          's Emergency Call ended
        </Box>
      )
    case IncidentActionType.Flag:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`AI Operator has `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            flagged{' '}
          </Box>
          the incident for further review and analysis.
        </Box>
      )
    case NonActionItem.IncidentNote:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          added a note.
        </Box>
      )
    case NonActionItem.SystemAction:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {systemMessage}
          </Box>
        </Box>
      )
    case NonActionItem.AudioMessage:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          sent a voice message.
        </Box>
      )
    case CannedIncidentActionType.Email:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`AI Operator `}
          </Box>
          {`sent an `}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            {` email notification `}
          </Box>
          to {`${creatorRoleName(roleName)} ${username}`}
        </Box>
      )
    case IncidentActionType.Mute:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {isRuleAction
              ? 'Automations'
              : `${creatorRoleName(roleName)} ${username}`}{' '}
          </Box>
          <InlineLink onClick={isRuleAction ? onRuleClick : undefined}>
            Muted{' '}
          </InlineLink>
          the incident.
        </Box>
      )
    case IncidentActionType.SendEmail:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {isRuleAction
              ? 'Automations'
              : `${creatorRoleName(roleName)} ${username}`}{' '}
          </Box>
          <InlineLink onClick={isRuleAction ? onRuleClick : undefined}>
            Emailed{' '}
          </InlineLink>
          {formatEmailRecipientCount(emailRecipientCount)}.
        </Box>
      )
    case IncidentActionType.SystemPriorityAssignment:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            System changed priority from No Priority to{' '}
            {formatPriorityValue(history?.newValue)} based on incident category
            default
          </Box>
        </Box>
      )
    case ExtendedIncidentActionType.PriorityChange: {
      const oldPriority = history?.oldValue
      const newPriority = history?.newValue
      if (isRuleAction) {
        // The BE omits the history row when the rule's value equals the
        // priority already applied, since the incident_history_values_differ
        // CHECK rejects an equal row (ENG-33958). There is no value to name in
        // that case, and the adjacent SYSTEM_PRIORITY_ASSIGNMENT row already
        // states the resulting priority.
        if (!newPriority) {
          return (
            <Box data-testid='ActionHeader:header'>
              <Box as='span' fontWeight='bold'>
                Automations{' '}
              </Box>
              <InlineLink onClick={onRuleClick}>set priority</InlineLink>.
            </Box>
          )
        }
        // States the resulting priority rather than narrating a transition.
        return (
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              Automations{' '}
            </Box>
            <InlineLink onClick={onRuleClick}>set priority </InlineLink>
            to{' '}
            <Box as='span' fontWeight='bold'>
              {formatPriorityValue(newPriority)}
            </Box>
            .
          </Box>
        )
      }
      if (oldPriority === null || oldPriority === undefined) {
        return (
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#2E6CDB' fontWeight='bold'>
              set priority{' '}
            </Box>
            as{' '}
            <Box as='span' fontWeight='bold'>
              {formatPriorityValue(newPriority)}
            </Box>
            .
          </Box>
        )
      }
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            changed the priority{' '}
          </Box>
          from{' '}
          <Box as='span' fontWeight='bold'>
            {formatPriorityValue(oldPriority)}
          </Box>{' '}
          to{' '}
          <Box as='span' fontWeight='bold'>
            {formatPriorityValue(newPriority)}
          </Box>
          .
        </Box>
      )
    }
  }
}

interface InlineLinkIProps {
  children: ReactNode
  onClick?: () => void
}

export const InlineLink = ({ children, onClick }: InlineLinkIProps) => (
  <Box
    _hover={onClick ? { textDecoration: 'underline' } : undefined}
    as='span'
    color='brand.100'
    cursor={onClick ? 'pointer' : 'auto'}
    fontWeight='bold'
    onClick={onClick}
  >
    {children}
  </Box>
)
